/**
 * TeamStrength Hub - API Client
 */
import axios from 'axios';

// Get backend URL dynamically at runtime
const getBackendUrl = () => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  // Fallback to current origin (same domain deployment)
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return '';
};

// Create axios instance with credentials
const api = axios.create({
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Set baseURL dynamically on each request
api.interceptors.request.use((config) => {
  const backendUrl = getBackendUrl();
  config.baseURL = `${backendUrl}/api`;
  
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect if we're on invite/signup pages or onboarding
      const currentPath = window.location.pathname;
      const isAuthFlow = currentPath.includes('/invite/') || 
                         currentPath.includes('/signup') || 
                         currentPath.includes('/onboarding') ||
                         currentPath.includes('/login');
      
      if (!isAuthFlow) {
        localStorage.removeItem('session_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ============ AUTH ============

export const authApi = {
  signup: (data) => api.post('/auth/signup', data),
  login: (data) => api.post('/auth/login', data),
  exchangeSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  getOrganizations: () => api.get('/auth/organizations'),
  
  // Password Reset
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  verifyResetToken: (token) => api.get(`/auth/reset-password/${token}/verify`),
  resetPassword: (token, password) => api.post(`/auth/reset-password/${token}`, { password }),
};

// ============ ORGANIZATIONS ============

export const orgApi = {
  list: () => api.get('/organizations'),
  get: (orgId) => api.get(`/organizations/${orgId}`),
  create: (data) => api.post('/organizations', data),
  update: (orgId, data) => api.put(`/organizations/${orgId}`, data),
  getUsage: (orgId) => api.get(`/organizations/${orgId}/usage`),
  
  // Members
  listMembers: (orgId) => api.get(`/organizations/${orgId}/members`),
  addMember: (orgId, data) => api.post(`/organizations/${orgId}/members`, data),
  updateMemberRole: (orgId, userId, role) => 
    api.put(`/organizations/${orgId}/members/${userId}`, { role }),
  removeMember: (orgId, userId) => api.delete(`/organizations/${orgId}/members/${userId}`),
};

// ============ STRENGTH MODEL ============

export const modelApi = {
  get: (orgId) => api.get(`/organizations/${orgId}/model`),
  previewCSV: (orgId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/organizations/${orgId}/model/preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  confirmCSV: (orgId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/organizations/${orgId}/model/confirm`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// ============ TEAMS ============

export const teamApi = {
  list: (orgId) => api.get(`/organizations/${orgId}/teams`),
  get: (orgId, teamId) => api.get(`/organizations/${orgId}/teams/${teamId}`),
  create: (orgId, data) => api.post(`/organizations/${orgId}/teams`, { ...data, org_id: orgId }),
  update: (orgId, teamId, data) => api.put(`/organizations/${orgId}/teams/${teamId}`, data),
  delete: (orgId, teamId) => api.delete(`/organizations/${orgId}/teams/${teamId}`),
  
  // Members
  listMembers: (orgId, teamId) => api.get(`/organizations/${orgId}/teams/${teamId}/members`),
  addMember: (orgId, teamId, data) => 
    api.post(`/organizations/${orgId}/teams/${teamId}/members`, { ...data, org_id: orgId }),
  removeMember: (orgId, teamId, userId) => 
    api.delete(`/organizations/${orgId}/teams/${teamId}/members/${userId}`),
  
  // Grid
  getGrid: (orgId, teamId) => api.get(`/organizations/${orgId}/teams/${teamId}/grid`),
  
  // Strengths
  getStrengths: (orgId, teamId, userId) => 
    api.get(`/organizations/${orgId}/teams/${teamId}/strengths/${userId}`),
  saveStrengths: (orgId, teamId, data) => 
    api.post(`/organizations/${orgId}/teams/${teamId}/strengths`, data),
  
  // AI
  generateReport: (orgId, teamId, reportType) => 
    api.post(`/organizations/${orgId}/teams/${teamId}/ai/generate`, { team_id: teamId, report_type: reportType }),
  listReports: (orgId, teamId) => api.get(`/organizations/${orgId}/teams/${teamId}/ai/reports`),
  
  // Team Chemistry
  generateChemistry: (orgId, teamId, data) => 
    api.post(`/teams/${teamId}/chemistry-analysis`, { org_id: orgId, ...data }),
  getChemistry: (orgId, teamId, language = 'de') => 
    api.get(`/teams/${teamId}/chemistry-analysis?org_id=${orgId}&language=${language}`),
};

// ============ STUDIO ============

export const studioApi = {
  getDashboard: (orgId) => api.get(`/organizations/${orgId}/studio`),
};

// ============ SUPERADMIN ============

export const superadminApi = {
  checkStatus: () => api.get('/superadmin/check'),
  makeAdmin: (userId) => api.post(`/superadmin/make/${userId}`),
};

// ============ USERS ============

export const userApi = {
  search: (query) => api.get(`/users/search?q=${encodeURIComponent(query)}`),
};

// ============ INVITATIONS ============

export const invitationApi = {
  create: (orgId, data) => api.post(`/organizations/${orgId}/invitations`, data),
  list: (orgId) => api.get(`/organizations/${orgId}/invitations`),
  revoke: (orgId, invitationId) => api.delete(`/organizations/${orgId}/invitations/${invitationId}`),
  getInfo: (token) => api.get(`/invitations/${token}/info`),
  accept: (token) => api.post(`/invitations/${token}/accept`),
};

// ============ MESSAGING CENTER ============

export const messagingApi = {
  getLogs: (orgId, skip = 0, limit = 50, emailType = null) => {
    let url = `/organizations/${orgId}/messaging/logs?skip=${skip}&limit=${limit}`;
    if (emailType) url += `&email_type=${emailType}`;
    return api.get(url);
  },
  getTemplates: (orgId) => api.get(`/organizations/${orgId}/messaging/templates`),
  updateTemplate: (orgId, templateType, data) => api.put(`/organizations/${orgId}/messaging/templates/${templateType}`, data),
  resetTemplate: (orgId, templateType) => api.post(`/organizations/${orgId}/messaging/templates/${templateType}/reset`),
  previewTemplate: (orgId, templateType, language = 'de') => api.get(`/organizations/${orgId}/messaging/templates/${templateType}/preview?language=${language}`),
  send: (orgId, data) => api.post(`/organizations/${orgId}/messaging/send`, data),
  getStats: (orgId) => api.get(`/organizations/${orgId}/messaging/stats`),
};

// ============ USER PROFILE ============

export const profileApi = {
  update: (data) => api.put('/auth/profile', data),
  complete: () => api.post('/auth/profile/complete'),
  saveStrengths: (data) => api.post('/auth/profile/strengths', data),
  getStrengths: (orgId) => api.get(`/auth/profile/strengths/${orgId}`),
  generateAnalysis: (data) => api.post('/profile/ai-analysis', data),
  getAnalysis: (orgId, language = 'de') => api.get(`/profile/ai-analysis/${orgId}?language=${language}`),
};

export default api;
